import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { Skeleton } from '@/components/ui/skeleton'
import { Skeleton as TextSkeleton, TextBars } from '../shared/skeleton'
import { SectionHeading, sectionTone, type SectionTone } from '../shared/section-heading'

// One spacing spec for every Settings page, so no section drifts from its neighbours.
/** Between the sections of a page. */
export const SETTINGS_STACK = 'flex flex-col gap-6'
/** Between the field rows (or list rows) inside a section. */
export const SETTINGS_FIELDS = 'flex flex-col gap-3'
/** A section's header inset, and its content's; the content's top padding is the gap under the heading. */
export const SETTINGS_HEADER = 'px-6 pt-6'
export const SETTINGS_BODY = 'p-6'

/**
 * A Settings card: the reports' section heading (its tinted tile in the section's hue) over the content, on a
 * neutral surface with a hairline border, in the landing's card language. A danger zone takes the rose hue.
 */
export function SettingsSection(props: {
  title?: ReactNode
  description?: ReactNode
  /** `null` draws the tinted tile without a glyph: a pending shell's, which keeps the icons off the eager path. */
  icon?: React.ElementType | null
  tone?: SectionTone
  actions?: ReactNode
  children: ReactNode
  variant?: 'default' | 'danger'
  className?: string
  testId?: string
}) {
  const danger = props.variant === 'danger'
  const tone = danger ? sectionTone('rose') : (props.tone ?? sectionTone('blue'))

  return (
    <div data-testid={props.testId} className={cn('bg-card rounded-2xl border', props.className)}>
      {props.title && props.icon !== undefined && (
        <div className={SETTINGS_HEADER}>
          <SectionHeading
            icon={props.icon}
            tone={tone}
            title={props.title}
            description={props.description}
            actions={props.actions}
          />
        </div>
      )}
      <div className={SETTINGS_BODY}>{props.children}</div>
    </div>
  )
}

/** A pending heading's tile: the Skeleton's own grey and pulse, at the tile's size, with no hue and no glyph. */
export const SKELETON_TILE: SectionTone = { text: '', bg: 'animate-pulse bg-muted' }

/**
 * A section while its page loads: the real card and heading (a plain skeleton tile, title and description as
 * text-height bars), over `rows` field rows shaped like the forms' own (label beside the field from `sm`, above it on
 * a phone). Built on SettingsSection, so its padding can't drift from the section it stands in for.
 */
export function SettingsSectionSkeleton(props: {
  /** How many field rows, or each row's field height in px (a text input is 32, a select or combobox 34). */
  rows?: number | number[]
  danger?: boolean
  /** A section whose body is one line (a sentence and a button) rather than field rows. */
  line?: boolean
  /** A description long enough to wrap to a second line on a phone. */
  longDescription?: boolean
  /** The section's own heading and description when the page's copy is known: its words, wrapping where they will. */
  title?: string
  description?: ReactNode
  children?: ReactNode
}) {
  const rows = typeof props.rows === 'number' || props.rows === undefined ? Array(props.rows ?? 4).fill(32) : props.rows
  return (
    <SettingsSection
      icon={null}
      tone={SKELETON_TILE}
      // A danger zone's card is the same card; only its hue differs, and a pending tile carries none.
      variant="default"
      title={props.title ? <TextBars>{props.title}</TextBars> : <TextSkeleton chars={14} />}
      description={
        props.description ? (
          <TextBars>{props.description}</TextBars>
        ) : props.longDescription ? (
          <>
            <TextSkeleton chars={32} />
            <span className="block sm:hidden">
              <TextSkeleton chars={20} />
            </span>
          </>
        ) : (
          <TextSkeleton chars={32} />
        )
      }
    >
      {props.children ??
        (props.line ? (
          <div className="flex items-center justify-between gap-4">
            <Skeleton className="h-4 w-56" />
            <Skeleton className="h-8 w-32 shrink-0 rounded-lg" />
          </div>
        ) : (
          <div className={SETTINGS_FIELDS}>
            {rows.map((height, i) => (
              // react-doctor-disable-next-line react-doctor/no-array-index-as-key -- a fixed placeholder list
              // oxlint-disable-next-line react/no-array-index-key -- a fixed placeholder list
              <div key={i} className="flex flex-col gap-2 sm:flex-row sm:items-start sm:gap-3">
                <div className="flex h-5 items-center sm:h-8 sm:w-48 sm:shrink-0">
                  <Skeleton className="h-3.5 w-28" />
                </div>
                <Skeleton className="min-w-0 rounded-lg sm:flex-1" style={{ height }} />
              </div>
            ))}
          </div>
        ))}
    </SettingsSection>
  )
}
